import React from 'react';
import { AGENT_CHAINS, labelForChain } from './agentChainLabels';
import './AgentChainChips.scss';

interface AgentChainPickerProps {
  /** Currently-selected chain keys. */
  value: string[];
  /** Called with the new full selection whenever a chip toggles. */
  onChange: (next: string[]) => void;
  /** Restrict to single-selection mode (used by Discover filter). Default false = multi-select. */
  single?: boolean;
  disabled?: boolean;
  label?: string;
  hint?: string;
  className?: string;
}

/**
 * Interactive multi-select for the fixed agent-chains allowlist.
 *
 * Unlike AgentCapabilityPicker, this doesn't fetch its taxonomy at
 * runtime — the chain list is small + curated + hardcoded on both
 * sides (see agentChainLabels.ts). Add a chain by editing the two
 * allowlists in sync; the server rejects unknown keys via Zod.
 */
const AgentChainPicker: React.FC<AgentChainPickerProps> = ({
  value,
  onChange,
  single = false,
  disabled = false,
  label,
  hint,
  className = '',
}) => {
  const handleToggle = (key: string) => {
    if (disabled) return;
    if (single) {
      onChange(value[0] === key ? [] : [key]);
      return;
    }
    if (value.includes(key)) {
      onChange(value.filter((k) => k !== key));
    } else {
      onChange([...value, key]);
    }
  };

  return (
    <div className={`agent-chain-picker ${className}`.trim()}>
      {label && <span className="agent-chain-picker__label">{label}</span>}
      <ul className="agent-chain-chips agent-chain-chips--md agent-chain-chips--interactive">
        {AGENT_CHAINS.map((key) => {
          const active = value.includes(key);
          return (
            <li key={key}>
              <button
                type="button"
                className={`agent-chain-chip agent-chain-chip--${key} agent-chain-chip--interactive${
                  active ? ' is-active' : ''
                }`}
                onClick={() => handleToggle(key)}
                disabled={disabled}
                aria-pressed={active}
              >
                {labelForChain(key)}
              </button>
            </li>
          );
        })}
      </ul>
      {hint && <p className="agent-chain-picker__hint">{hint}</p>}
    </div>
  );
};

export default AgentChainPicker;
